import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  Group,
  List,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBook2,
  IconCheckbox,
  IconClipboardList,
  IconInfoCircle,
  IconPackage,
  IconShieldCheck,
  IconTruck,
} from '@tabler/icons';
import { useState } from 'react';

interface TutorialStep {
  icon: React.ReactNode;
  title: string;
  description: string;
  tips?: string[];
}

interface EpiTutorialProps {
  screen: 'catalog' | 'list' | 'deliveries' | 'matrix' | 'rounds';
}

const TUTORIALS: Record<
  EpiTutorialProps['screen'],
  { title: string; subtitle: string; steps: TutorialStep[] }
> = {
  catalog: {
    title: 'Catálogo de EPIs',
    subtitle: 'Como cadastrar e gerenciar os equipamentos disponíveis',
    steps: [
      {
        icon: <IconPackage size={18} />,
        title: 'O que é o catálogo?',
        description:
          'O catálogo centraliza todos os tipos de EPI da empresa. Cada item cadastrado aqui pode ser vinculado a funcionários e incluído em rondas de inspeção.',
      },
      {
        icon: <IconBook2 size={18} />,
        title: 'Cadastrando um novo EPI',
        description:
          'Clique em "Novo EPI" e preencha nome, categoria, marca, número do CA (Certificado de Aprovação) e o período de reposição em dias.',
        tips: [
          'O CA é obrigatório por lei — mantenha-o atualizado.',
          'O período de reposição define quando o status muda para "Atrasado" na Gestão de EPIs.',
        ],
      },
      {
        icon: <IconCheckbox size={18} />,
        title: 'Editando ou removendo',
        description:
          'Use os ícones de edição e exclusão ao lado de cada item. Remover um EPI do catálogo não exclui vínculos já existentes com funcionários.',
        tips: [
          'Antes de remover, verifique se há funcionários com este EPI vinculado na tela de Gestão.',
        ],
      },
    ],
  },
  list: {
    title: 'Gestão de EPIs',
    subtitle: 'Como acompanhar e registrar entregas por funcionário',
    steps: [
      {
        icon: <IconShieldCheck size={18} />,
        title: 'Visão geral',
        description:
          'A tela lista todos os funcionários que possuem EPIs vinculados. Cada linha é um funcionário — clique sobre o nome para expandir e ver os EPIs dele.',
        tips: [
          'Os funcionários são exibidos em grupos de 10. Use a paginação no final da página para navegar.',
          'Use o filtro "Funcionário" para localizar alguém diretamente pelo nome.',
        ],
      },
      {
        icon: <IconTruck size={18} />,
        title: 'Registrando uma entrega',
        description:
          'Para registrar que um EPI foi entregue: (1) localize o funcionário pelo filtro ou na lista, (2) clique no nome dele para abrir o painel, (3) clique em "Marcar Entregue" ao lado do EPI desejado, (4) informe a data e confirme.',
        tips: [
          'Você pode informar uma data retroativa — ideal para registrar entregas que já aconteceram.',
          'O motivo da entrega é opcional, mas ajuda no histórico (ex: Admissão, Reposição).',
        ],
      },
      {
        icon: <IconInfoCircle size={18} />,
        title: 'Entendendo os status',
        description:
          'Cada EPI recebe um status automático com base na última entrega:',
        tips: [
          '🟢 Entregue — dentro do prazo de reposição.',
          '🟡 Pendente — nenhuma entrega registrada ainda.',
          '🔴 Atrasado — prazo de reposição superado.',
          '⚪ Sem prazo — EPI sem período de reposição no catálogo.',
        ],
      },
      {
        icon: <IconCheckbox size={18} />,
        title: 'Encontrando quem precisa de EPI',
        description:
          'Use o filtro "Status" para ver rapidamente quem está com EPI atrasado ou pendente. Isso evita verificar funcionário por funcionário.',
        tips: [
          'Filtre por "Atrasado" para priorizar quem está com o EPI vencido.',
          'Filtre por setor + status para focar em uma área específica da empresa.',
        ],
      },
    ],
  },
  deliveries: {
    title: 'Histórico de Entregas',
    subtitle: 'Como consultar o registro completo de entregas realizadas',
    steps: [
      {
        icon: <IconClipboardList size={18} />,
        title: 'O que é o histórico?',
        description:
          'Esta tela exibe todas as entregas de EPI realizadas na empresa, com data, funcionário, EPI, motivo e quantidade. Serve como comprovante e auditoria.',
      },
      {
        icon: <IconBook2 size={18} />,
        title: 'Filtrando registros',
        description:
          'Use os filtros de período (data início e fim), funcionário e tipo de EPI para localizar entregas específicas.',
        tips: [
          'Para auditorias, filtre por um intervalo de datas e exporte os dados visíveis.',
          'O histórico não pode ser editado — é um registro imutável de cada entrega.',
        ],
      },
    ],
  },
  matrix: {
    title: 'Matriz de EPIs',
    subtitle: 'Como definir quais EPIs são obrigatórios por setor',
    steps: [
      {
        icon: <IconShieldCheck size={18} />,
        title: 'O que é a matriz?',
        description:
          'A matriz define quais EPIs são exigidos em cada setor da empresa. É a base para identificar funcionários sem o equipamento correto.',
      },
      {
        icon: <IconBook2 size={18} />,
        title: 'Adicionando um requisito',
        description:
          'Selecione o setor, escolha o EPI no catálogo e clique em adicionar. O sistema marcará como pendente todo funcionário daquele setor que não tiver o EPI vinculado.',
        tips: [
          'Mantenha a matriz atualizada ao contratar para novos setores.',
          'Um EPI precisa estar no catálogo antes de ser adicionado à matriz.',
        ],
      },
      {
        icon: <IconAlertTriangle size={18} />,
        title: 'Removendo um requisito',
        description:
          'Clique no ícone de exclusão ao lado do EPI na lista do setor. Isso não remove os vínculos já existentes com funcionários, apenas deixa de exigir o EPI para novos.',
      },
    ],
  },
  rounds: {
    title: 'Rondas de EPI',
    subtitle: 'Como registrar inspeções periódicas de uso de EPIs',
    steps: [
      {
        icon: <IconShieldCheck size={18} />,
        title: 'O que são rondas?',
        description:
          'Rondas são inspeções realizadas em campo para verificar se os funcionários estão usando os EPIs corretamente e em boas condições. Cada ronda gera um registro com os itens verificados.',
      },
      {
        icon: <IconBook2 size={18} />,
        title: 'Criando uma ronda',
        description:
          'Clique em "Nova Ronda", preencha a data, unidade, setor e responsável. Em seguida, adicione os itens verificados: selecione o funcionário, o EPI e a situação encontrada.',
        tips: [
          'Adicione quantos itens quiser antes de salvar — não há limite por ronda.',
          'Você pode registrar a situação "OK" para confirmar que o EPI está em boas condições.',
        ],
      },
      {
        icon: <IconAlertTriangle size={18} />,
        title: 'Situações disponíveis',
        description: 'Cada item pode receber uma das seguintes situações:',
        tips: [
          '✅ OK — EPI em uso correto e boas condições.',
          '🟡 Desgastado — Uso prolongado, substituição recomendada.',
          '🔴 Danificado — EPI inutilizável, substituição imediata.',
          '🔴 Funcionário sem EPI — EPI não estava sendo usado.',
          '🟠 Necessita reposição — Estoque insuficiente.',
          '🟠 Uso inadequado — EPI usado de forma errada.',
        ],
      },
      {
        icon: <IconClipboardList size={18} />,
        title: 'Aba de Pendências',
        description:
          'A aba "Pendências" lista todos os itens de rondas que não estão com situação "OK". Use como painel de ações para acompanhar e resolver cada problema identificado.',
        tips: [
          'A coluna "Ação" sugere o que fazer para cada situação (substituir, orientar, repor).',
          'As pendências somem automaticamente quando uma nova ronda registra o item como "OK".',
        ],
      },
    ],
  },
};

const SCREEN_COLORS: Record<EpiTutorialProps['screen'], string> = {
  catalog: 'blue',
  list: 'green',
  deliveries: 'cyan',
  matrix: 'violet',
  rounds: 'orange',
};

export function EpiTutorial({ screen }: EpiTutorialProps) {
  const [open, setOpen] = useState(false);
  const tutorial = TUTORIALS[screen];
  const color = SCREEN_COLORS[screen];

  return (
    <>
      <Button
        variant="light"
        color={color}
        leftIcon={<IconBook2 size={16} />}
        onClick={() => setOpen(true)}
      >
        Como usar
      </Button>

      <Drawer
        opened={open}
        onClose={() => setOpen(false)}
        title={
          <Group spacing={10}>
            <ThemeIcon color={color} variant="light" size="lg" radius="xl">
              <IconBook2 size={18} />
            </ThemeIcon>
            <div>
              <Text fw={700} size="md" lh={1.2}>
                {tutorial.title}
              </Text>
              <Text size="xs" color="dimmed">
                Tutorial de uso
              </Text>
            </div>
          </Group>
        }
        position="right"
        size="xl"
        padding="xl"
      >
        <Text size="sm" color="dimmed" mb={20}>
          {tutorial.subtitle}
        </Text>

        <Stack spacing={0}>
          {tutorial.steps.map((step, index) => (
            <Box key={index}>
              <Group spacing={12} align="flex-start" noWrap mb={4}>
                <ThemeIcon
                  color={color}
                  variant="light"
                  size="md"
                  radius="xl"
                  mt={2}
                >
                  {step.icon}
                </ThemeIcon>
                <div style={{ flex: 1 }}>
                  <Group spacing={8} mb={4}>
                    <Badge color={color} variant="dot" size="sm">
                      Passo {index + 1}
                    </Badge>
                    <Text fw={600} size="sm">
                      {step.title}
                    </Text>
                  </Group>
                  <Text size="sm" color="dimmed" mb={step.tips ? 8 : 0}>
                    {step.description}
                  </Text>
                  {step.tips && (
                    <List
                      size="sm"
                      spacing={4}
                      icon={
                        <ThemeIcon
                          color={color}
                          size={16}
                          radius="xl"
                          variant="light"
                        >
                          <IconInfoCircle size={10} />
                        </ThemeIcon>
                      }
                    >
                      {step.tips.map((tip, tipIndex) => (
                        <List.Item key={tipIndex}>
                          <Text size="xs" color="dimmed">
                            {tip}
                          </Text>
                        </List.Item>
                      ))}
                    </List>
                  )}
                </div>
              </Group>
              {index < tutorial.steps.length - 1 && (
                <Divider my={16} variant="dashed" />
              )}
            </Box>
          ))}
        </Stack>
      </Drawer>
    </>
  );
}
